import React from 'react';
import useResponsive from '../hooks/useResponsive';

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const ClassSelection = ({ 
  userClasses, 
  selectedClass, 
  onClassSelect 
}) => {
  const { isMobile, isTablet } = useResponsive();

  if (userClasses.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-32 h-32 bg-green-cloud/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className="fas fa-graduation-cap text-green-cloud text-4xl"></i>
        </div>
        <h3 className="text-2xl font-bold text-grey-dusk mb-4">
          No classes available
        </h3>
        <p className="text-gray-600 mb-8">
          You are not enrolled in any classes yet. Contact your
          administrator to get enrolled.
        </p>
      </div>
    );
  }

  const getGridCols = () => {
    if (isMobile) return 'grid-cols-1';
    if (isTablet) return 'grid-cols-1 sm:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8 mb-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-grey-dusk mb-2">
          Select Your Class
        </h2>
        <p className="text-gray-600 text-sm sm:text-base">
          Choose a class to view your subjects and grades
        </p>
      </div>

      {/* Class Selection Cards */}
      <div className={`grid ${getGridCols()} gap-4 sm:gap-6 mb-6 sm:mb-8`}>
        {userClasses.map((classItem) => (
          <div key={classItem.id} className="group">
            <div
              className={`card-brand cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                selectedClass?.id === classItem.id
                  ? "ring-2 ring-sunrise-red bg-sunrise-red/5"
                  : "hover:shadow-lg"
              }`}
              onClick={() => onClassSelect(classItem)}
            >
              <div className="flex flex-col h-full p-3 sm:p-4">
                {/* Class Header */}
                <div className="flex items-start sm:items-center mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sunrise-red-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <i className="fas fa-graduation-cap text-sunrise-red-600 text-lg sm:text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-grey-dusk group-hover:text-sunrise-red transition-colors duration-300 text-sm sm:text-base leading-tight">
                      {classItem.schoolName}
                    </h3>
                    <p className="text-sunrise-red font-medium text-sm sm:text-base mt-1">
                      {classItem.name}
                    </p>
                    {/* Period info - show on mobile as well but smaller */}
                    {classItem.startDate && classItem.endDate && (
                      <p className="text-xs text-gray-500 mt-1 sm:hidden">
                        {formatDate(classItem.startDate)} - {formatDate(classItem.endDate)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Class Details - Hidden on mobile, visible on larger screens */}
                {classItem.startDate && classItem.endDate && (
                  <div className="hidden sm:block mb-4">
                    <p className="text-sm text-gray-600">
                      <i className="fas fa-calendar mr-2"></i>
                      {formatDate(classItem.startDate)} - {formatDate(classItem.endDate)}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-auto pt-3 sm:pt-4 border-t border-gray-200">
                  <p className="text-xs sm:text-sm text-gray-500 flex items-center">
                    <i className="fas fa-mouse-pointer mr-2"></i>
                    {selectedClass?.id === classItem.id 
                      ? (isMobile ? 'Tap to close' : 'Click to close')
                      : (isMobile ? 'Tap to view' : 'Click to view subjects')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message when no class is selected but classes are available */}
      {userClasses.length > 0 && !selectedClass && (
        <div className="text-center py-8 sm:py-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-hand-pointer text-sunrise-red text-xl sm:text-2xl"></i>
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-grey-dusk mb-2">
            Please select a class
          </h3>
          <p className="text-gray-600 text-sm sm:text-base px-4">
            Choose a class from the list above to view your grades and manage subjects.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClassSelection;